import { useCallback, useEffect, useRef, useState, type JSX } from 'react';
import { SimpleToast } from '@/components/ui';
import { removeProductFromList } from '@/services/slaydAPI';
import useSlaylistStore from '@/stores/SlaylistProductStore';
import useSlaylistSelectorStore from '@/stores/SlaylistSelectorStore';
import useToastStore, { type Toast } from '@/stores/ToastStore';
import hostStyles from './ToastContainer.module.css';

const ManagedToast = ({
  toast,
  onRemove,
}: {
  toast: Toast;
  onRemove: (id: number) => void;
}): JSX.Element => {
  const { openSlaylistSelector } = useSlaylistSelectorStore();
  const { addToSlaylist } = useSlaylistStore();
  const [fadeOut, setFadeOut] = useState(false);

  const wasUndone = useRef(false);

  const removeWithFade = useCallback((): void => {
    setFadeOut(true);
    setTimeout(() => {
      onRemove(toast.id);
    }, 400);
  }, [onRemove, toast.id]);

  const handleButtonClick = (): void => {
    if (!toast.button) return;

    const { action } = toast.button;

    switch (action.type) {
      case 'change': {
        openSlaylistSelector({
          productId: action.productId,
          productImage: action.productImage,
        });
        break;
      }

      case 'undo': {
        wasUndone.current = true;
        const { product, index, slaylistId } = action;
        addToSlaylist(product, index);

        // Restore the product override for the slaylist it was removed from
        const slaylists = useSlaylistSelectorStore.getState().getSlaylists();
        const slaylist = slaylists.find((s) => s.id === slaylistId);
        if (slaylist) {
          useSlaylistSelectorStore
            .getState()
            .setProductSlaylist(product.id, slaylist.name, slaylist.id);
        }
        break;
      }

      default:
        break;
    }

    onRemove(toast.id);
  };

  useEffect(() => {
    const timerId = setTimeout(() => {
      if (toast.button?.action.type === 'undo' && !wasUndone.current) {
        const { slaylistId, product } = toast.button.action;

        removeProductFromList(slaylistId, product.id).catch((error) => {
          console.error('Backend delete failed:', error);
        });

        // Also remove from productSlaylistOverrides when backend deletion happens
        useSlaylistSelectorStore.getState().removeProductSlaylist(product.id);
      }

      removeWithFade();
    }, 3000);

    return () => {
      clearTimeout(timerId);
    };
  }, [toast, removeWithFade]);

  return (
    <SimpleToast
      message={toast.message}
      fadeOut={fadeOut}
      buttonText={toast.button?.text}
      onButtonClick={handleButtonClick}
    />
  );
};

const ToastContainer = (): JSX.Element => {
  const { toasts, removeToast } = useToastStore();

  if (toasts.length === 0) {
    return <></>;
  }

  return (
    <div
      className={hostStyles.host}
      aria-live="polite"
      aria-relevant="additions text"
    >
      {toasts.map((toast) => (
        <div key={toast.id} className={hostStyles.slot}>
          <ManagedToast toast={toast} onRemove={removeToast} />
        </div>
      ))}
    </div>
  );
};

export default ToastContainer;
